import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import ApiContactUs from '../../../api/api-contact-us';
import { selectMyProfile } from '../../../store/Account/selectors';
import { selectActiveAllMap } from '../../../store/Map/SuperMap/selectors';
import { LoginModalMode } from '../../Registration/login-enum';
import LoginRegisterDialog from '../../Registration/login-register-dialog';
import { PulseLoader } from '../pulse-loader';
import { StyledButton } from '../styled-button';
import { isMobileVersion } from '../../../lib/soar-helper';

interface ReportMapContentProps {
    handleReportIsOpen: () => void;
}

const ReportMapContent = (props: ReportMapContentProps) => {
    const [reportReason, setReportReason] = useState<string[]>([]);
    const [showLogin, setShowLogin] = useState<boolean>(false);
    const [isSubmittingReport, setIsSubmittingReport] = useState<boolean>(false);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);

    const myProfile = useSelector(selectMyProfile);
    const selectedMap = useSelector(selectActiveAllMap);

    const reportOptions: string[] = [
        'Map violates copyright',
        'Map violates community guidelines',
        'Map is not displaying correctly',
        'Map is in the wrong location',
    ];

    const handleSelectedReason = (reason: string) => {
        const exists = reportReason.find((report) => report === reason);
        if (exists) {
            const updatedReasons = reportReason.filter((report) => report !== exists);
            setReportReason(updatedReasons);
            return;
        }
        setReportReason([...reportReason, reason]);
    };

    const handleSubmitReport = () => {
        if (!myProfile) {
            setShowLogin(true);
            return;
        }

        if (!reportReason.length) {
            toast.error('Please select the reasons for this report');
            return;
        }

        if (!selectedMap || !myProfile) {
            toast.error('Something had gone wrong with reporting this map, Please try again');
            return;
        }

        if (myProfile.email) {
            setIsSubmittingReport(true);
            ApiContactUs.reportProblem(myProfile.email, reportReason.toString(), selectedMap.id)
                .then(() => {
                    handleSubmittedSuccess();
                })
                .catch(() => {
                    toast.error('Something had gone wrong with reporting this map, Please try again');
                })
                .finally(() => {
                    setIsSubmittingReport(false);
                    setReportReason([]);
                });
        }
    };

    const handleSubmittedSuccess = () => {
        setIsSubmittingReport(false);
        setShowSuccess(true);

        // Timeout for visual success notification
        setTimeout(() => {
            setReportReason([]);
            setShowSuccess(false);
            props.handleReportIsOpen();
        }, 3000);
    };

    return (
        <ReportContainer>
            {isSubmittingReport && !showSuccess ? (
                <ReportSubmitting>
                    <PulseLoader iconSize="20px" />
                </ReportSubmitting>
            ) : (
                <React.Fragment>
                    {!isMobileVersion ? <ReportTitle>Report this map</ReportTitle> : null}
                    {showSuccess && !isSubmittingReport ? (
                        <ReportSubmitted>
                            <ReportIcon className="fa fa-exclamation-triangle" aria-hidden="true" />
                            <ReportSubmittedText>
                                Your report has been <br /> submitted!
                            </ReportSubmittedText>
                            <ReportSubmittedText>Our team will look into it as soon as possible</ReportSubmittedText>
                        </ReportSubmitted>
                    ) : (
                        <React.Fragment>
                            <ReportMenuItem>
                                {reportOptions.map((option, index) => {
                                    return (
                                        <ReportOption key={index}>
                                            <ReportCheckbox
                                                value={option}
                                                type="checkbox"
                                                onChange={(e) => handleSelectedReason(e.target.value)}
                                            />
                                            {option}
                                        </ReportOption>
                                    );
                                })}
                            </ReportMenuItem>
                            <ReportSubmitButton onClick={handleSubmitReport}>SUBMIT</ReportSubmitButton>
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
            {showLogin && (
                <LoginRegisterDialog
                    isOpen={true}
                    initialMode={LoginModalMode.LOGIN}
                    onClose={() => setShowLogin(false)}
                />
            )}
        </ReportContainer>
    );
};

export default ReportMapContent;

const ReportContainer = styled.div`
    min-height: 190px;
`;

const ReportMenuItem = styled.div`
    margin: 10px;
`;

const ReportOption = styled.div`
    font-size: 16px;
    padding: 2px 0px 2px 0px;
    font-weight: 400;
    display: flex;
    flex-direction: row;
    line-height: 21px;
    color: rgba(255, 255, 255, 0.87);
    margin-bottom: 8px;
`;

const ReportCheckbox = styled.input`
    margin-right: 8px;
`;

const ReportSubmitButton = styled(StyledButton)`
    display: block;
    margin: 0 auto;
`;

const ReportSubmitting = styled.div`
    padding-top: 20%;
`;

const ReportSubmitted = styled.div`
    text-align: center;
`;

const ReportSubmittedText = styled.div`
    font-size: 16px;
    padding: 5px;
    font-weight: 400;
    line-height: 21px;
    color: rgba(255, 255, 255, 0.87);
`;

const ReportIcon = styled.i`
    font-size: 50px;
    color: #eed926;
    margin: 15px;
`;

const ReportTitle = styled.h4`
    margin-top: 10px;
    color: rgba(255, 255, 255, 0.87);
`;
